import { useCallback, useEffect, useState } from "react";
import { DollarSign } from "lucide-react";
import { adminApi } from "../../lib/adminApi";

const Metric = ({ label, value, gold, testId }) => (
  <div className="rounded-3xl border border-cream/10 p-6" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-cream/40">{label}</p>
    <p className={`mt-3 font-serif text-4xl ${gold ? "text-gold" : "text-cream"}`}>{value}</p>
  </div>
);

export const RevenueTab = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await adminApi.get("/admin/revenue");
    setData(res.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <p className="text-sm text-cream/50">Loading revenue…</p>;

  if (!data.configured) {
    return (
      <div className="rounded-3xl border border-cream/10 px-6 py-12 text-center" data-testid="revenue-not-configured">
        <DollarSign className="mx-auto h-8 w-8 text-gold/50" />
        <p className="mt-4 font-serif text-2xl text-cream">Stripe isn't connected yet.</p>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-cream/50">
          Once Hearts+ payments are live, this panel shows monthly recurring revenue, active subscribers, trial conversions, and cancellations — straight from Stripe.
        </p>
      </div>
    );
  }

  const mrrText = Object.entries(data.mrr || {})
    .map(([c, n]) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${c}`)
    .join(" · ") || "$0";

  return (
    <div data-testid="revenue-tab">
      <div className="mb-6 flex items-center gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Revenue overview</p>
        <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${data.mode === "live" ? "border-gold/40 bg-gold/10 text-gold" : "border-cream/20 text-cream/50"}`} data-testid="revenue-mode">
          {data.mode} mode
        </span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Monthly recurring revenue" value={mrrText} gold testId="revenue-mrr" />
        <Metric label="Active subscribers" value={data.active_subscriber_count} testId="revenue-active" />
        <Metric label="In free trial" value={data.trialing_count} testId="revenue-trialing" />
        <Metric label="Trial → paid (30d)" value={data.trial_to_paid_conversions_30d} testId="revenue-conversions" />
      </div>
      <div className="mt-8 rounded-3xl border border-cream/10 p-6" data-testid="revenue-cancellations">
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-cream/40">Recent cancellations (30d)</p>
        {data.recent_cancellations.length === 0 ? (
          <p className="text-sm text-cream/40">None — nothing has churned.</p>
        ) : (
          data.recent_cancellations.map((c) => (
            <div key={c.id} className="flex items-center justify-between border-b border-cream/5 py-3 text-sm last:border-0">
              <span className="text-cream/70">{c.id}</span>
              <span className="text-cream/40">{new Date(c.canceled_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {c.reason || "no reason given"}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
